import { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { AlertTriangle, CheckCircle2, Eye, FileUp, Loader2, Paperclip, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { mirrorItem, sumsubHub } from "@/lib/api";
import { MirrorFilePreview } from "@/pages/applications/MirrorFilePreview";
import { nice } from "./sumsubShared";

const KIND_FALLBACK = {
  IDENTITY: { label: "Identity document", types: ["PASSPORT", "DRIVERS", "ID_CARD", "RESIDENCE_PERMIT"], sub_types: ["FRONT_SIDE", "BACK_SIDE"] },
  PROOF_OF_RESIDENCE: { label: "Proof of address", types: ["UTILITY_BILL"], sub_types: [] },
  OTHER: { label: "Other", types: ["OTHER", "SELFIE", "AGREEMENT", "CONTRACT", "INCOME_SOURCE"], sub_types: [] },
};
const TYPE_LABEL = { UTILITY_BILL: "Proof of address (utility bill · bank statement · council tax · tenancy)", DRIVERS: "Driving licence", ID_CARD: "National ID card",
  RESIDENCE_PERMIT: "Residence permit", OTHER: "Other document", INCOME_SOURCE: "Proof of income" };
const COUNTRIES = [["GBR", "United Kingdom"], ["AUS", "Australia"], ["IRL", "Ireland"], ["NZL", "New Zealand"], ["USA", "United States"], ["IND", "India"], ["PAK", "Pakistan"],
  ["NGA", "Nigeria"], ["ZAF", "South Africa"], ["POL", "Poland"], ["ROU", "Romania"], ["LTU", "Lithuania"], ["PRT", "Portugal"], ["ITA", "Italy"], ["ESP", "Spain"], ["FRA", "France"], ["DEU", "Germany"]];
const fmtWhen = (s) => (s ? new Date(s).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");

function MondayFiles({ files, loading, error, selected, toggle, onPreview }) {
  if (loading) return <div className="flex items-center gap-2 py-3 text-xs text-slate-500" data-testid="sumsub-adddoc-monday-loading"><Loader2 className="w-3.5 h-3.5 animate-spin" />Loading the deal's Monday files…</div>;
  if (error) return <div className="py-2 text-xs text-rose-700" data-testid="sumsub-adddoc-monday-error">{error}</div>;
  if (!files.length) return <div className="py-2 text-xs text-slate-500" data-testid="sumsub-adddoc-monday-empty">No files on the Monday item yet.</div>;
  return (
    <div className="max-h-56 divide-y overflow-y-auto rounded border" data-testid="sumsub-adddoc-monday-list">
      {files.map((f) => (
        <label key={f.id} className={`flex cursor-pointer items-center gap-2 px-2.5 py-1.5 text-xs hover:bg-slate-50 ${selected.has(String(f.id)) ? "bg-sky-50" : ""}`} data-testid={`sumsub-adddoc-monday-${f.id}`}>
          <Checkbox checked={selected.has(String(f.id))} onCheckedChange={() => toggle(String(f.id))} data-testid={`sumsub-adddoc-monday-tick-${f.id}`} />
          <span className="min-w-0 flex-1 truncate" title={f.name}>{f.name}</span>
          {f.uploaded_by && <span className="shrink-0 rounded border border-violet-200 bg-violet-50 px-1 text-[10px] text-violet-700">{f.uploaded_by}</span>}
          <span className="hidden shrink-0 text-[10px] tabular-nums text-slate-400 sm:inline">{fmtWhen(f.created_at)}</span>
          <span className="shrink-0 text-[10px] text-slate-400">{f.size ? `${Math.round(f.size / 1024)} KB` : ""}</span>
          <Button type="button" size="sm" variant="ghost" className="h-6 px-1.5 text-[11px]" onClick={(e) => { e.preventDefault(); onPreview(f); }} title="Preview here (nothing is downloaded)" data-testid={`sumsub-adddoc-monday-preview-${f.id}`}>
            <Eye className="w-3.5 h-3.5" />
          </Button>
        </label>
      ))}
    </div>
  );
}

function UploadPicker({ uploads, setUploads }) {
  const ref = useRef(null);
  const add = (list) => { const picked = Array.from(list || []); setUploads((u) => [...u, ...picked].slice(0, 10)); };
  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-center gap-2 rounded border border-dashed border-slate-300 p-2" onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); add(e.dataTransfer.files); }} data-testid="sumsub-adddoc-dropzone">
        <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => ref.current?.click()} data-testid="sumsub-adddoc-pick-files"><FileUp className="w-3.5 h-3.5 mr-1" />Choose files…</Button>
        <span className="text-[11px] text-slate-500">or drop PDF / JPG / PNG here (up to 40 MB each)</span>
        <input ref={ref} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.heic,.webp,application/pdf,image/*" className="hidden" onChange={(e) => { add(e.target.files); e.target.value = ""; }} data-testid="sumsub-adddoc-file-input" />
      </div>
      {uploads.length > 0 && (
        <ul className="divide-y rounded border text-xs" data-testid="sumsub-adddoc-upload-list">
          {uploads.map((f, i) => (
            <li key={`${f.name}-${i}`} className="flex items-center gap-2 px-2.5 py-1.5" data-testid={`sumsub-adddoc-upload-${i}`}>
              <Paperclip className="w-3 h-3 shrink-0 text-slate-400" /><span className="min-w-0 flex-1 truncate">{f.name}</span>
              <span className="text-[10px] text-slate-400">{Math.round(f.size / 1024)} KB</span>
              <button type="button" className="text-slate-400 hover:text-rose-600" onClick={() => setUploads((u) => u.filter((_, j) => j !== i))} data-testid={`sumsub-adddoc-upload-remove-${i}`}><X className="w-3.5 h-3.5" /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Result({ r }) {
  if (!r) return null;
  return (
    <div className="space-y-1 rounded border border-emerald-200 bg-emerald-50 p-2 text-xs" data-testid="sumsub-adddoc-result">
      <div className="flex items-center gap-1.5 font-medium text-emerald-800"><CheckCircle2 className="w-3.5 h-3.5" />{r.added.length} document{r.added.length === 1 ? "" : "s"} added to Sumsub</div>
      {r.warnings?.length > 0 && <div className="flex items-start gap-1.5 text-amber-800" data-testid="sumsub-adddoc-warnings"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /><span>Sumsub flagged: {r.warnings.join(" · ")}</span></div>}
      {r.recheck && (r.recheck.ok
        ? <div className="text-sky-800" data-testid="sumsub-adddoc-recheck-ok">Re-check requested — the applicant is now {r.recheck.status || "pending"}; the result lands back here via the webhook.</div>
        : <div className="text-amber-800" data-testid="sumsub-adddoc-recheck-error">Document saved, but Sumsub would not start a re-check: {String(r.recheck.error || "").replace(/\.\s*$/, "")}. Use Reset step or ask the applicant to finish the remaining steps.</div>)}
    </div>
  );
}

// "Add document" — the Sumsub dashboard action, from the deal's Monday files (tick, preview inline) or files on this computer.
// After adding, Sumsub is asked to check again (optional). Works for UK and AU: the applicant's workspace picks the account.
export function SumsubAddDocDialog({ applicant, steps = [], linked, preset, onClose, onDone }) {
  const docSteps = useMemo(() => steps.filter((s) => s.doc_types?.length), [steps]);
  const kinds = useMemo(() => {
    const out = {};
    for (const k of ["IDENTITY", "PROOF_OF_RESIDENCE"]) {
      const st = docSteps.find((s) => s.key === k);
      out[k] = { ...KIND_FALLBACK[k], label: st?.label || KIND_FALLBACK[k].label, types: st?.doc_types?.length ? st.doc_types : KIND_FALLBACK[k].types, failed: st?.answer === "RED" };
    }
    out.OTHER = KIND_FALLBACK.OTHER;
    return out;
  }, [docSteps]);
  const [kind, setKind] = useState(preset && kinds[preset] ? preset : "PROOF_OF_RESIDENCE");
  const [docType, setDocType] = useState(kinds[preset && kinds[preset] ? preset : "PROOF_OF_RESIDENCE"].types[0]);
  const [subType, setSubType] = useState("");
  const [country, setCountry] = useState((applicant.country || "").toUpperCase() || (applicant.workspace === "AUS" ? "AUS" : "GBR"));
  const [recheck, setRecheck] = useState(true);
  const [files, setFiles] = useState({ list: [], loading: !!linked?.item_id, error: null });
  const [selected, setSelected] = useState(new Set());
  const [uploads, setUploads] = useState([]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!linked?.item_id) return;
    let dead = false;
    mirrorItem(linked.item_id).then((it) => { if (!dead) setFiles({ list: it.files || [], loading: false, error: null }); })
      .catch((e) => { if (!dead) setFiles({ list: [], loading: false, error: e.response?.data?.detail || e.message }); });
    return () => { dead = true; };
  }, [linked?.item_id]);

  const pickKind = (k) => { setKind(k); setDocType(kinds[k].types[0]); setSubType(""); };
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const total = selected.size + uploads.length;

  const submit = async () => {
    if (!total) { toast.error("Tick a Monday file or choose a file to upload"); return; }
    if (country.length !== 3) { toast.error("Country must be a 3-letter code, e.g. GBR or AUS"); return; }
    setBusy(true);
    let r = { added: [], warnings: [], recheck: null };
    try {
      const base = { doc_type: docType, country, sub_type: subType };
      if (selected.size) {
        const monday = files.list.filter((f) => selected.has(String(f.id))).map((f) => ({ item_id: linked.item_id, asset_id: String(f.id), name: f.name }));
        const a = await sumsubHub.addDocs(applicant.id, { ...base, recheck: recheck && !uploads.length, monday });
        r = { added: [...r.added, ...a.added], warnings: [...r.warnings, ...(a.warnings || [])], recheck: a.recheck, applicant: a.applicant };
        setSelected(new Set());
      }
      if (uploads.length) {
        const fd = new FormData();
        uploads.forEach((f) => fd.append("files", f, f.name));
        fd.append("doc_type", docType); fd.append("country", country); fd.append("sub_type", subType); fd.append("recheck", recheck ? "true" : "false");
        const u = await sumsubHub.uploadDocs(applicant.id, fd);
        r = { added: [...r.added, ...u.added], warnings: [...r.warnings, ...(u.warnings || [])], recheck: u.recheck, applicant: u.applicant };
        setUploads([]);
      }
      setResult(r);
      toast.success(`${r.added.length} document${r.added.length === 1 ? "" : "s"} added${r.recheck?.ok ? " · re-check requested" : ""}`);
      onDone?.(r);
    } catch (e) {
      if (r.added.length) { setResult(r); onDone?.(r); }
      toast.error(e.response?.data?.detail || e.message, { duration: 9000 });
    }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="sumsub-adddoc-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base"><Upload className="w-4 h-4 text-sky-600" />Add a document for {applicant.full_name || "the applicant"}</DialogTitle>
          <DialogDescription className="text-xs">Same as the dashboard's Add document — the file goes onto the applicant in Sumsub {applicant.workspace === "AUS" ? "Australia" : "UK"}, then Sumsub checks it again. Nothing is sent to the customer.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">What is it?</div>
            <div className="flex flex-wrap gap-1.5" data-testid="sumsub-adddoc-kinds">
              {Object.entries(kinds).map(([k, v]) => (
                <button key={k} type="button" onClick={() => pickKind(k)} className={`rounded-full border px-3 py-1 text-xs transition-colors ${kind === k ? "border-sky-500 bg-sky-50 text-sky-900" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`} data-testid={`sumsub-adddoc-kind-${k}`}>
                  {v.label}{v.failed && <span className="ml-1.5 rounded bg-red-100 px-1 text-[10px] text-red-700">failed</span>}
                </button>
              ))}
            </div>
          </div>
          <div className="grid gap-2 sm:grid-cols-3">
            <label className="text-xs">
              <span className="mb-0.5 block text-[11px] text-slate-500">Document type</span>
              <select value={docType} onChange={(e) => setDocType(e.target.value)} className="h-8 w-full rounded border bg-white px-2 text-xs" data-testid="sumsub-adddoc-type">
                {kinds[kind].types.map((t) => <option key={t} value={t}>{TYPE_LABEL[t] || nice(t)}</option>)}
              </select>
            </label>
            <label className="text-xs">
              <span className="mb-0.5 block text-[11px] text-slate-500">Issuing country</span>
              <Input list="sumsub-adddoc-countries" value={country} onChange={(e) => setCountry(e.target.value.toUpperCase().slice(0, 3))} className="h-8 text-xs uppercase" placeholder="GBR" data-testid="sumsub-adddoc-country" />
              <datalist id="sumsub-adddoc-countries">{COUNTRIES.map(([c, n]) => <option key={c} value={c}>{n}</option>)}</datalist>
            </label>
            {kinds[kind].sub_types.length > 0 && (
              <label className="text-xs">
                <span className="mb-0.5 block text-[11px] text-slate-500">Side</span>
                <select value={subType} onChange={(e) => setSubType(e.target.value)} className="h-8 w-full rounded border bg-white px-2 text-xs" data-testid="sumsub-adddoc-subtype">
                  <option value="">Single page / not applicable</option>
                  {kinds[kind].sub_types.map((t) => <option key={t} value={t}>{nice(t)}</option>)}
                </select>
              </label>
            )}
          </div>
          <div>
            <div className="mb-1 flex items-center justify-between text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              <span>Monday files on {linked?.sf || "the deal"}</span>
              {selected.size > 0 && <span className="normal-case tracking-normal text-sky-700" data-testid="sumsub-adddoc-selected-count">{selected.size} ticked</span>}
            </div>
            {linked?.item_id ? <MondayFiles files={files.list} loading={files.loading} error={files.error} selected={selected} toggle={toggle} onPreview={setPreview} />
              : <div className="py-2 text-xs text-slate-500" data-testid="sumsub-adddoc-no-link">This applicant isn't attached to an application yet — attach it first to pick from Monday, or upload below.</div>}
          </div>
          <div>
            <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">From this computer</div>
            <UploadPicker uploads={uploads} setUploads={setUploads} />
          </div>
          <label className="flex items-center gap-2 text-xs" data-testid="sumsub-adddoc-recheck">
            <Switch checked={recheck} onCheckedChange={setRecheck} data-testid="sumsub-adddoc-recheck-switch" />
            <span>Ask Sumsub to check again straight after adding <span className="text-slate-500">(turn off to only add the file)</span></span>
          </label>
          <Result r={result} />
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={busy} data-testid="sumsub-adddoc-cancel">{result ? "Close" : "Cancel"}</Button>
          <Button size="sm" className="bg-sky-600 text-white hover:bg-sky-700" onClick={submit} disabled={busy || !total} data-testid="sumsub-adddoc-submit">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
            {busy ? "Adding…" : total ? `Add ${total} ${total === 1 ? "document" : "documents"}${recheck ? " & re-check" : ""}` : "Add document"}
          </Button>
        </DialogFooter>
        {preview && <MirrorFilePreview file={preview} onClose={() => setPreview(null)} />}
      </DialogContent>
    </Dialog>
  );
}
